import styled from "styled-components";
import EmptyState from "@adsk/alloy-react-empty-state";
import { default as Tabs, Tab } from "@adsk/alloy-react-tabs";
import { OverflowTooltip } from "@adsk/alloy-react-tooltip";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Issue, IssueAttributes } from "@adsk/acc-issues-types";
import { useIsFetchingIssue } from "../hooks/useIsFetchingIssue";
import { useIsMetadataLoading } from "@adsk/acc-issues-common/hooks/state/useIsMetadataLoading";
import {
  fetchIssueByIdAction,
  getIsCreatingIssueById,
  getIsFetchingIssueById,
  getIssueFetchErrorStatusByIssueId,
  maybeRemoveFromLogAfterUnpublishAction,
} from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
import {
  useDispatch,
  useSelector,
} from "@adsk/acc-issues-core/state/redux/hooks";
import { StatusCodes } from "../utils/browser/consts";
import { CenteredEmptyState } from "@adsk/acc-issues-common/components/display/EmptyStates";
import { useElementWidth } from "../utils/hooks/useElementWidth";
import { ActivityLog } from "./ActivityLog/ActivityLog";
import {
  getPanelWidthVariation,
  PanelWidthVariation,
} from "./panelWidthVariations";
import { IssueDetailTabPanelBody } from "./IssueDetailTabPanel/IssueDetailTabPanelBody";
import { useTranslations } from "@adsk/acc-issues-translations";
import {
  IssuesConsumerProps,
  withIssuesConsumer,
} from "@adsk/acc-issues-common/components/primitive/IssuesConsumer";
import { trackDrawerView } from "../utils/analytics/issueNavigationTracking";
import { defaultLoader } from "./DefaultLoader";
import { ErrorBoundary } from "@adsk/acc-issues-common/components/primitive/ErrorBoundary";
import { MarkupCreationPayload } from "@adsk/acc-issues-types";
import { DeletedIssuesSpaceContext } from "@adsk/acc-issues-core/providers/DeletedIssuesSpaceContext";
import { IssueDetailPanelTabs } from "./IssueDetailPanelTabType";
import {
  ActiveDetailPanelTabProvider,
  useActiveDetailPanelTab,
} from "./ActiveDetailPanelTabContext";
import { issueLogContext } from "../IssuesTab/IssueLog/issueLogContext";
import { useIsIssueJustCreated } from "./IssueDetailTabPanel/hooks/useIsIssueJustCreated";
import { useMonitoring } from "@adsk/acc-issues-core/utils/hooks/useMonitoring";
import { useIssueData } from "../hooks/useIssueData";
import { RenderIconType } from "@adsk/alloy-react-icon";

export const useIssueDetailPanelTitle = (issueId?: string) => {
  const { T } = useTranslations();
  const getIssueData = useIssueData();
  const issue = getIssueData(issueId);
  const isFetchingIssue = useIsFetchingIssue(issueId);
  const isDeletedSpaceActive = useContext(DeletedIssuesSpaceContext);

  return issue && (!issue.deleted || (issue.deleted && isDeletedSpaceActive))
    ? T.translate("issueEditForm.panelTitle", {
        issueNumber: issue.displayId,
      })
    : isFetchingIssue
    ? T.translate("issueEditForm.loading")
    : "";
};

export interface IssueDetailPanelProps {
  /**
   * The ID of the issue being rendered.
   */
  issueId: string;
  /**
   * Callback to be called when the issue is fetched
   * @param issue The issue that was fetched.
   */
  onIssueFetched?: (issue: Issue) => void;
  /**
   * Callback to be called when the issue is deleted.
   * @param issueId
   */
  onDelete?: (issueId: string) => void;
  /**
   * Callback to be called when the issue gets published or unpublished.
   * @param issueId
   * @param isNowPublished
   */
  onChangePublishStatus?: (issueId: string, isNowPublished: boolean) => void;
  /**
   * Callback to be called after each change
   * @param issueChanges the payload that gets sent to the issues service
   */
  onSuccessfulChange?: (
    issueChanges: Partial<IssueAttributes>,
    issueSubResources?: {
      placements?: MarkupCreationPayload | null;
    }
  ) => void;
  /**
   * Defines whether the placement renders as a clickable deeplink. Default is false.
   */
  hasPlacementField?: boolean;
  onRestoreSuccess?: () => void;
  customTabs?: {
    id: string;
    title: string;
    renderIcon?: RenderIconType;
    renderPanel: (issue: Issue) => React.ReactNode;
  }[];
}

const CustomTab = styled(Tab)`
  position: relative;
  height: 100%;
  overflow-x: hidden;
`;

/**
 * Displays the details for a single issue. Fetches the issue on the first load if it's missing.
 */
const IssueDetailPanelComponent = ({
  issueId,
  onIssueFetched,
  onDelete,
  onChangePublishStatus,
  onSuccessfulChange,
  hasPlacementField = false,
  renderLoader,
  onRestoreSuccess,
  customTabs,
}: IssueDetailPanelProps & IssuesConsumerProps) => {
  const { T } = useTranslations();
  const getIssueData = useIssueData();
  const issue = getIssueData(issueId);
  const { logger } = useMonitoring();
  const isMetadataLoading = useIsMetadataLoading();
  const isFetchingIssue = useSelector(getIsFetchingIssueById)(issueId);
  const isCreatingIssue = useSelector(getIsCreatingIssueById)(issueId);

  const { isInIssuesLog } = useContext(issueLogContext);
  const focusTitleOnCreation = !isInIssuesLog;
  const isIssueJustCreated = useIsIssueJustCreated(issue);

  const issueFetchErrorStatus = useSelector(getIssueFetchErrorStatusByIssueId)(
    issueId
  );
  // we may already have the issue data and don't wait to make the panel
  // show as a loader until the most recent data is fetched, which is why
  // we are doing `(isFetchingIssue && !issue)` instead of just `isFetchingIssue`
  const isLoading = (isFetchingIssue && !issue) || isMetadataLoading;
  const isDeletedSpaceActive = useContext(DeletedIssuesSpaceContext);

  const [activeIssueDetailPanelTab, setIssueDetailPanelTab] =
    useActiveDetailPanelTab();

  // determine if this detail panel is in a skinny context or not.
  // If it is skinny there are display implications.
  const { ref, width } = useElementWidth();
  const [widthVariation, setWidthVariation] = useState(
    PanelWidthVariation.Normal
  );
  useEffect(() => {
    if (width) {
      setWidthVariation(getPanelWidthVariation(width! as number));
    }
  }, [width, setWidthVariation]);

  // fetch the issue each time the detail panel is shown.
  // We want the most up-to-date data every time we view the issue.
  const dispatch = useDispatch();

  useEffect(
    () => {
      if (issueId && !isCreatingIssue) {
        trackDrawerView({
          issueUid: issueId,
          deleted: issue?.deleted ?? false,
        });
        dispatch(
          fetchIssueByIdAction({
            id: issueId,
            shouldFetchSheetAssociation: true,
            includeDeletedMarkups: isDeletedSpaceActive,
          })
        );
      }
    },
    // Issue as a dep trigger several effects since the object is recreated
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [issueId, dispatch, hasPlacementField, isCreatingIssue]
  );

  useEffect(
    () => {
      if (
        issueId &&
        onIssueFetched &&
        !isFetchingIssue &&
        issue &&
        issue.id === issueId
      ) {
        onIssueFetched(issue);
      }
    },
    // Issue as a dep trigger several effects since the object is recreated
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [issueId, onIssueFetched, isFetchingIssue]
  );

  const handleIssuePublishedStatusChange = useCallback(
    (_, isNowPublished) => {
      onChangePublishStatus?.(issueId, isNowPublished);

      if (!isNowPublished) {
        dispatch(
          maybeRemoveFromLogAfterUnpublishAction({
            id: issueId!,
            onRemove: () => onDelete?.(issueId),
          })
        );
      }
    },
    [issueId, onDelete, dispatch, onChangePublishStatus]
  );

  const handleTabClick = useCallback(
    (previewTab: IssueDetailPanelTabs | string) => {
      setIssueDetailPanelTab(previewTab);
    },
    [setIssueDetailPanelTab]
  );

  //On unmount or on issue change, reset the tab to the default tab
  useEffect(() => {
    return setIssueDetailPanelTab(IssueDetailPanelTabs.Details);
  }, [setIssueDetailPanelTab, issueId]);

  if (isLoading) {
    return <>{renderLoader!()}</>;
  }

  if (!issue) {
    if (isCreatingIssue) {
      return <>{renderLoader!()}</>;
    }
    switch (issueFetchErrorStatus) {
      case StatusCodes.NOT_FOUND:
        return (
          <CenteredEmptyState
            title={T.translate("issueEditForm.emptyState.notFound.title")}
            description={T.translate(
              "issueEditForm.emptyState.notFound.description"
            )}
            illustrationType={EmptyState.ILLUSTRATION_TYPES.FOLDER_PHOTO_GREY}
          />
        );
      case StatusCodes.FORBIDDEN:
        return (
          <CenteredEmptyState
            title={T.translate("issueEditForm.emptyState.noPermission.title")}
            description={T.translate(
              "issueEditForm.emptyState.noPermission.description"
            )}
            illustrationType={EmptyState.ILLUSTRATION_TYPES.TOOLBOX_GREY}
          />
        );
      default:
        return (
          <CenteredEmptyState
            title={T.translate("somethingWentWrong")}
            description={T.translate(
              "issueEditForm.emptyState.error.description"
            )}
            illustrationType={
              EmptyState.ILLUSTRATION_TYPES.DESKTOP_COMPUTER_GREY
            }
          />
        );
    }
  }

  if (issue.deleted && !isDeletedSpaceActive) {
    return (
      <CenteredEmptyState
        title={T.translate("issueEditForm.emptyState.deleted.title")}
        description={T.translate(
          "issueEditForm.emptyState.deleted.description"
        )}
        illustrationType={EmptyState.ILLUSTRATION_TYPES.FOLDER_PHOTO_GREY}
      />
    );
  }

  return (
    <ErrorBoundary
      key={issue.id}
      scope="issue-detail-panel"
      title={T.translate("errorBoundary.sidePanelError.title") as string}
      description={
        T.translate("errorBoundary.sidePanelError.description") as string
      }
      logError={logger.error}
    >
      <Tabs
        ref={ref}
        labelWidth="full"
        style={{ height: "100%", minHeight: 0 }}
        className={`IssueDetailPanel issue-${issue.id} width-${widthVariation}`}
        active={activeIssueDetailPanelTab}
      >
        <Tab
          data-testid="details-tab"
          key={IssueDetailPanelTabs.Details}
          tab={IssueDetailPanelTabs.Details}
          label={T.translate("issueEditForm.tabs.details")}
          style={{ height: "100%" }}
          onClick={() => handleTabClick(IssueDetailPanelTabs.Details)}
        >
          <IssueDetailTabPanelBody
            data-testid="issue-detail-panel"
            key={issue.id}
            issue={issue}
            onDelete={onDelete}
            onChangePublishStatus={handleIssuePublishedStatusChange}
            onSuccessfulChange={onSuccessfulChange}
            withPlacementLink={hasPlacementField}
            widthVariation={widthVariation}
            onRestoreSuccess={onRestoreSuccess}
            panelOpenedAfterIssueCreation={
              focusTitleOnCreation && isIssueJustCreated
            }
          />
        </Tab>
        <Tab
          data-testid="activity-log-tab"
          key={IssueDetailPanelTabs.ActivityLog}
          tab={IssueDetailPanelTabs.ActivityLog}
          label={T.translate("issueEditForm.tabs.activityLog")}
          onClick={() => handleTabClick(IssueDetailPanelTabs.ActivityLog)}
          style={{
            position: "relative",
            height: "100%",
            padding: "0 18px",
            overflowX: "hidden",
          }}
        >
          <ActivityLog data-testid="activity-log" issueId={issueId} />
        </Tab>
        {customTabs &&
          customTabs.map(customTab => {
            return (
              <CustomTab
                data-testid={`custom-tab`}
                key={`${IssueDetailPanelTabs.CustomTab}-${customTab.id}`}
                tab={`${IssueDetailPanelTabs.CustomTab}-${customTab.id}`}
                label={
                  <div style={{ overflow: "hidden" }}>
                    <OverflowTooltip content={customTab.title}>
                      {customTab.title}
                    </OverflowTooltip>
                  </div>
                }
                renderIcon={customTab.renderIcon}
                onClick={() => {
                  handleTabClick(
                    `${IssueDetailPanelTabs.CustomTab}-${customTab.id}`
                  );
                }}
              >
                <ErrorBoundary
                  scope="issue-detail-panel-custom-tab"
                  key={issue.id}
                  title={
                    T.translate("errorBoundary.sidePanelError.title") as string
                  }
                  description={
                    T.translate(
                      "errorBoundary.sidePanelError.description"
                    ) as string
                  }
                  logError={logger.error}
                >
                  {customTab.renderPanel(issue)}
                </ErrorBoundary>
              </CustomTab>
            );
          })}
      </Tabs>
    </ErrorBoundary>
  );
};

const IssueDetailPanelWithProvider = withIssuesConsumer<IssueDetailPanelProps>(
  props => {
    return (
      <ActiveDetailPanelTabProvider>
        <IssueDetailPanelComponent {...props} />
      </ActiveDetailPanelTabProvider>
    );
  },
  defaultLoader
);

export const IssueDetailPanel = React.memo(IssueDetailPanelWithProvider);
